/**
 * The row actions menu for the Files browser.
 *
 * Opens from either a right-click on a row or the ⋯ button on hover, so a user
 * who has never right-clicked still finds the actions. Delete uses an inline
 * confirm step rather than window.confirm, and Move to… opens a scoped list of
 * destination folders rather than asking for a typed path.
 */

import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { api, type VfsFileEntry } from '../shared/api-client';
import { FileIconComponent, fileIconKindFor } from './FileIconComponent';
import './FileRowMenuComponent.css';

export interface FileRowMenuComponentProps {
  entry: VfsFileEntry;
  /** Every entry currently selected when the menu opened. If more than one,
   *  the menu offers a bulk Delete that names the count. */
  selectedEntries: VfsFileEntry[];
  /** Element to anchor the popover to. */
  anchor: HTMLElement;
  /** Current directory absolute path — used to label "move into a sibling". */
  currentDir: string;
  /** Candidate destination folders for Move to… (writable roots + current dir's folders). */
  moveTargets: Array<{ path: string; name: string }>;
  onRename: (entry: VfsFileEntry) => void;
  onDownload: (entry: VfsFileEntry) => void;
  onMoveTo: (entry: VfsFileEntry, toDir: string) => void;
  onDelete: (entries: VfsFileEntry[]) => Promise<void>;
  onClose: () => void;
}

type Mode = 'main' | 'move' | 'confirm-delete';

export function FileRowMenuComponent(props: FileRowMenuComponentProps): ReactElement {
  const { entry, selectedEntries, anchor, moveTargets, onRename, onDownload, onMoveTo, onDelete, onClose } = props;
  // Bulk delete applies only when the menu'd row is part of a multi-selection —
  // the common case of "select several, right-click one". If the user opened the
  // menu on a row outside the selection, treat it as a single-row action.
  const bulkEntries = selectedEntries.length > 1 && selectedEntries.some((e) => e.path === entry.path)
    ? selectedEntries
    : [entry];
  const [mode, setMode] = useState<Mode>('main');
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Position the popover below the anchor, clamped to the viewport so a row at
  // the bottom edge does not push the menu off-screen.
  const pos = useMemo(() => {
    const r = anchor.getBoundingClientRect();
    const menuW = 220;
    const menuH = mode === 'main' ? 180 : 240;
    const left = Math.min(r.left, window.innerWidth - menuW - 8);
    const top = r.bottom + 4 + menuH > window.innerHeight ? Math.max(8, r.top - menuH - 4) : r.bottom + 4;
    return { left, top };
  }, [anchor, mode]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const targets = useMemo(() => {
    // Never offer moving an entry into itself or into one of its descendants.
    return moveTargets.filter((t) => t.path !== entry.path && !t.path.startsWith(`${entry.path}/`));
  }, [moveTargets, entry.path]);

  const doDelete = async () => {
    setBusy(true);
    await onDelete(bulkEntries);
    setBusy(false);
    onClose();
  };

  return (
    <div className="file-row-menu-popover" style={{ left: pos.left, top: pos.top }} ref={ref} role="menu">
      {mode === 'main' && (
        <>
          <button type="button" className="file-row-menu-item" onClick={() => { onRename(entry); onClose(); }}>
            <FileIconComponent kind="text" size={14} /> Rename
          </button>
          {entry.kind === 'file' && (
            <button type="button" className="file-row-menu-item" onClick={() => { onDownload(entry); }}>
              <FileIconComponent kind="generic" size={14} /> Download
            </button>
          )}
          <button type="button" className="file-row-menu-item" disabled={targets.length === 0} onClick={() => setMode('move')}>
            <FileIconComponent kind="folder" size={14} /> Move to…
          </button>
          <button type="button" className="file-row-menu-item file-row-menu-item--danger" onClick={() => setMode('confirm-delete')}>
            <FileIconComponent kind="generic" size={14} /> Delete
          </button>
        </>
      )}

      {mode === 'move' && (
        <div className="file-row-menu-sub">
          <div className="file-row-menu-sub-head">
            <button type="button" className="file-row-menu-back" onClick={() => setMode('main')}>‹ Back</button>
            <span className="file-row-menu-sub-title">Move “{entry.name}” to</span>
          </div>
          <div className="file-row-menu-sub-list">
            {targets.length === 0 && <div className="file-row-menu-sub-empty">No other writable folders.</div>}
            {targets.map((t) => (
              <button
                key={t.path}
                type="button"
                className="file-row-menu-item"
                onClick={() => { onMoveTo(entry, t.path); onClose(); }}
              >
                <FileIconComponent kind={fileIconKindFor('directory', '', t.name)} size={14} /> {t.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {mode === 'confirm-delete' && (
        <div className="file-row-menu-confirm">
          <p className="file-row-menu-confirm-text">
            {bulkEntries.length === 1
              ? `Delete ${entry.kind === 'directory' ? `“${entry.name}” and everything in it` : `“${entry.name}”`}?`
              : `Delete ${bulkEntries.length} selected items?`}
          </p>
          <div className="file-row-menu-confirm-actions">
            <button type="button" className="file-row-menu-item file-row-menu-item--danger" disabled={busy} onClick={doDelete}>
              {busy ? 'Deleting…' : 'Delete'}
            </button>
            <button type="button" className="file-row-menu-item" disabled={busy} onClick={() => setMode('main')}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Trigger a browser download for a file entry. Used by the menu's Download item. */
export function downloadVfsEntry(entry: VfsFileEntry): void {
  if (entry.kind !== 'file') return;
  window.open(api.getVfsDownloadUrl(entry.path), '_blank');
}
